// Wrapper that gates an interactive button behind sign-in.
// (PR2 / PUBLIC-DECKS plan, §5.3.)
//
// Two modes, picked from Clerk's `useUser()`:
//
//  - Signed-in: renders children unchanged. Whatever onClick the
//    children carry runs as before — GatedAction is a passthrough.
//  - Signed-out: renders the SAME children visually but on top of an
//    overlay span that absorbs the click, queues the intent in
//    localStorage, and opens Clerk's sign-in modal. The original
//    children's onClick never fires (which is correct — it would
//    have called an authed API and 401'd anyway). After sign-up,
//    Clerk redirects back to the same URL where <ActionReplayer>
//    reads the queued slot and dispatches.
//
// We use the `pointer-events: none` trick to neuter the inner
// children visually (so they still render normally) without
// double-handling clicks: the wrapping span captures all pointer
// events and the children become inert. This avoids any reliance on
// React event-bubbling semantics that vary by Clerk's
// SignInButton implementation.

import { SignInButton, useUser } from "@clerk/clerk-react";
import type { ReactNode } from "react";
import {
  queueAction,
  type QueuedActionKind,
} from "../lib/queued-action";

export interface GatedActionProps {
  /** Action kind — must match the allow-list in queued-action.ts. */
  action: QueuedActionKind;
  /** Action-specific payload. The replayer validates per-kind. */
  payload: Record<string, unknown>;
  /** Deck slug this action belongs to. The replayer cross-checks
   *  against the current page's deckId so a queued action never
   *  misfires on a different deck. */
  deckId: string;
  /** Owner username (the `:owner` segment of the URL). Stored
   *  alongside `deckId` so the replayer can verify both segments. */
  owner: string;
  /** Disable BOTH branches. Useful when the underlying form has
   *  empty/invalid input — the signed-in branch wouldn't have
   *  fired either, and the signed-out branch shouldn't queue an
   *  empty payload. */
  disabled?: boolean;
  children: ReactNode;
}

export default function GatedAction({
  action,
  payload,
  deckId,
  owner,
  disabled,
  children,
}: GatedActionProps) {
  const { isSignedIn } = useUser();

  // Signed-in: no-op passthrough. The child's own onClick fires
  // exactly as it did before GatedAction was wrapped around it.
  if (isSignedIn) {
    return <>{children}</>;
  }

  // Signed-out: render the children inside a clickable SignInButton
  // wrapper. The children are visually present but pointer-inert
  // (`pointer-events-none` on a wrapping div) so the SignInButton's
  // own click handler is the only listener that fires. We queue the
  // intent in localStorage immediately on the click (capture phase)
  // before Clerk opens its modal.
  const redirectUrl =
    typeof window !== "undefined"
      ? window.location.pathname + window.location.search
      : "/";

  return (
    <SignInButton
      mode="modal"
      forceRedirectUrl={redirectUrl}
      signUpForceRedirectUrl={redirectUrl}
    >
      <span
        // `inline-block` so wrapped buttons keep their natural
        // sizing; `cursor-pointer` so the area still feels clickable
        // even though the inner button is pointer-inert.
        className={
          disabled
            ? "inline-block cursor-not-allowed opacity-60"
            : "inline-block cursor-pointer"
        }
        onClickCapture={(e) => {
          if (disabled) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
          // Stash before the modal opens. If the user closes the
          // modal without signing up, the queued action will sit in
          // localStorage until TTL_MS (10 min) expires.
          queueAction({ action, payload, deckId, owner });
          // Don't stopPropagation — SignInButton's own listener is
          // bubble-phase and we want it to fire.
        }}
      >
        <span className="pointer-events-none inline-block">{children}</span>
      </span>
    </SignInButton>
  );
}
